import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { forwardRef } from "react";

import type {
  AdminRouteAccessArgs,
  SidebarNavigationItem,
} from "@/routing/adminRoutes";
import {
  ShellAccountBlock,
  type ShellAccountIdentity,
} from "@/vnext/app-shell/ShellAccountBlock";
import { ShellSidebarNav } from "@/vnext/app-shell/ShellSidebarNav";
import { ShellSidebarSearch } from "@/vnext/app-shell/ShellSidebarSearch";
import { useSidebarBadges } from "@/vnext/app-shell/useSidebarBadges";

/**
 * The sidebar (F-001, F-003 to F-017): brand block, search, sections and
 * pages, and the signed-in block pinned to the bottom. Collapsed, it is the
 * 56px icon rail (F-200).
 */
export const ShellSidebar = forwardRef<
  HTMLButtonElement,
  {
    account: {
      buildLabel: string;
      environmentLine: string;
      identity: ShellAccountIdentity;
      isLoggingOut: boolean;
      onSignOut: () => void;
    };
    /** Live alerts count badge (Navigation PRD §6). */
    canReadLiveAlerts?: boolean;
    canSearchRecords: boolean;
    collapsed: boolean;
    hiddenPaths?: ReadonlySet<string>;
    id: string;
    items: SidebarNavigationItem[];
    onCloseNarrow: () => void;
    onSearchRecords: (query: string) => void;
    routeAccess: AdminRouteAccessArgs;
  }
>(function ShellSidebar(
  {
    account,
    canReadLiveAlerts = false,
    canSearchRecords,
    collapsed,
    hiddenPaths,
    id,
    items,
    onCloseNarrow,
    onSearchRecords,
    routeAccess,
  },
  closeButtonRef,
) {
  const badgedItems = useSidebarBadges(items, canReadLiveAlerts);
  return (
    <aside aria-label="Admin navigation" className="vnext-sidebar" id={id}>
      <div className="vnext-shell-brand">
        <div aria-hidden="true" className="vnext-shell-brand__mark">
          MC
        </div>
        <div className="vnext-shell-brand__copy">
          <div className="vnext-shell-brand__name">Mission Control</div>
          <div className="vnext-shell-brand__sub">HelloCashflow</div>
        </div>
        <button
          aria-label="Close navigation drawer"
          className="vnext-sidebar__narrow-close"
          onClick={onCloseNarrow}
          ref={closeButtonRef}
          type="button"
        >
          <CloseOutlinedIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <ShellSidebarSearch
        canSearchRecords={canSearchRecords}
        collapsed={collapsed}
        hiddenPaths={hiddenPaths}
        onSearchRecords={onSearchRecords}
        routeAccess={routeAccess}
      />

      <nav aria-label="Primary admin navigation" className="vnext-sidebar__nav">
        <ShellSidebarNav collapsed={collapsed} items={badgedItems} />
      </nav>

      <ShellAccountBlock collapsed={collapsed} {...account} />
    </aside>
  );
});
